import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Box
} from '@mui/material';
import {
  IErrorMessage,
  useErrorMessage
} from '@shared/hooks/use-error-message';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@shared/utils/trim-form-values-utils';
import { RoutePaths } from '@utils/route.utils.ts';

const InternetProviderFormContent: React.FC<{
  onSave: (data: { apiKey: string }) => void;
  onBack: () => void;
  item: any;
  onFormValidChange?: (valid: boolean) => void;
  isLoading: boolean;
}> = ({ onSave, onBack, item, onFormValidChange, isLoading }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<{ apiKey: string }>({ mode: 'onChange' });

  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const errorMessages: IErrorMessage = {
    apiKey: {
      required: 'validation_api_key_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    }
  };

  useEffect(() => {
    onFormValidChange?.(isValid);
  }, [isValid, onFormValidChange]);

  const handleSave: SubmitHandler<{ apiKey: string }> = (data) => {
    const trimmedData = trimFormValues(data);
    onSave(trimmedData);
  };

  return (
    <form
      className="flex flex-col h-full justify-between gap-4"
      onSubmit={handleSubmit(handleSave)}
    >
      <div className="flex flex-col gap-1">
        <FormControl error={!!errors.apiKey}>
          <FormLabel className="flex items-center gap-1">
            <span className="required-symbol">*</span>
            {t('label_api_key')}
            <InfoTooltip text="tooltip_info_api_key" />
          </FormLabel>
          <OutlinedInput
            {...register('apiKey', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.apiKey}
            placeholder={t('placeholder_api_key')}
          />
          <FormHelperText>
            {errors?.apiKey &&
              getErrorMessage({
                errors,
                fieldName: 'apiKey',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <div className="inline-flex justify-end items-end">
          <a
            href={`${RoutePaths.Core.ContactUs.absolutePath}?category=integrations&integrationName=${item?.name}`}
            className="underline text-sm text-[var(--primary)] cursor-pointer hover:!opacity-70"
          >
            {t('contact_to_get_api_key')}
          </a>
        </div>
      </div>

      <Box className="flex justify-center items-center gap-4 mt-[54px]">
        <Button variant="outlined" onClick={onBack}>
          {t('Back')}
        </Button>
        <Button
          type="submit"
          loading={isLoading}
          disabled={!isValid}
          variant="contained"
        >
          {t('import')}
        </Button>
      </Box>
    </form>
  );
};

export default InternetProviderFormContent;
